import Results from '@pelckmans/elementis/components/results';
import { ComponentProps } from 'react';
import { SCORE_STATUS } from '../../../../constants/ScoreStatus';
import useIsMobile from '../../../../hooks/useIsMobile';
import { getResultPercentages, isQuantifiable, Status } from '../../../../utils/results';

export interface ScorableProgressProps {
  unachievable?: boolean;
  score?: {
    maximum: number;
    achievable: number;
    achieved: number;
    status?: Status;
    phases?: {
      id: number;
      name: string;
      stage: number;
      duration: number;
      score: { maximum: number; achievable: number; achieved: number };
    }[];
    isLastPhase?: boolean;
  };
  variant?: 'donut' | 'bar';
  responsive?: boolean;
  showTooltip?: boolean;
  fluid?: boolean;
}

export default function ScorableProgress({
  unachievable = false,
  score,
  variant = 'bar',
  responsive = true,
  showTooltip = false,
  fluid = false,
}: ScorableProgressProps) {
  const isMobile = useIsMobile();

  const status = score?.status ?? 'not-started';
  const tooltipShown = showTooltip || !isMobile;

  const commonProps: Partial<ComponentProps<typeof Results>> = {
    status,
    showTooltip: tooltipShown,
    responsive,
    ...(variant === 'bar' ? { variant: 'bar', fluid } : { variant: 'donut' }),
    ...((score?.phases || []).length && {
      adaptivePhases: (score?.phases || []).map(x => ({
        ...x,
        score: getResultPercentages(x.score),
      })),
    }),
    isLastPhase: score?.isLastPhase,
  };

  if (isQuantifiable({ score, unachievable, status })) {
    return (
      <Results
        {...commonProps}
        quantifiable
        {...getResultPercentages(score, status === SCORE_STATUS.PENDING_GRADING)}
      />
    );
  }

  return <Results {...commonProps} quantifiable={false} />;
}
